import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { requestTypes, currencies } from "../../../utilities/RequestData.js";
import {
  addSalaryAdvanceRequest,
  fetchSalaryAdvanceRequests,
} from "../../../../contents/redux/actions/salaryAdvanceRequestActions";
import { fetchRoles } from "../../../../contents/redux/actions/roleActions";
import {
  LiveHelpRounded,
  MonetizationOnRounded,
  PaymentsRounded,
} from "@mui/icons-material";
import { toast } from "react-toastify";
import styles from "../../../utilities/cssmodules/SalaryAdvance.module.css";

const SalaryAdvance = () => {
  const location = useLocation();
  const { requestTypeId } = location.state || {};
  console.log("Received Request Type ID:", requestTypeId);
  const userId = localStorage.getItem("userId");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [inputs, setInputs] = useState({
    amount: "",
    currency: "",
    purpose: "",
  });
  const [isFormValid, setIsFormValid] = useState(false);

  const roles = useSelector((state) => state.roles.roles);

  // Fetch roles from API when the component mounts
  useEffect(() => {
    dispatch(fetchRoles());
  }, [dispatch]);

  // Validate form
  useEffect(() => {
    const isValid = inputs.amount && inputs.currency && inputs.purpose;
    setIsFormValid(isValid);
  }, [inputs]);

  // Handle input changes
  const handleChange = (event) => {
    const { name, value } = event.target;
    setInputs((prev) => ({ ...prev, [name]: value }));
  };

  // Handle save form
  const handleSave = (e) => {
    e.preventDefault();
    if (!isFormValid) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (!requestTypeId) {
      toast.error("Request Type ID is missing.");
      return;
    }
    if (!userId) {
      toast.error("User ID is missing. Please log in again.");
      return;
    }

    const requestData = {
      amount: parseFloat(inputs.amount),
      currency: inputs.currency,
      purpose: inputs.purpose,
      isOnSaved: true,
      requestType: parseInt(requestTypeId),
      userId: parseInt(userId),
    };

    dispatch(addSalaryAdvanceRequest(requestData))
      .then(() => {
        setInputs({ amount: "", currency: "", purpose: "" });
        toast.success("Salary advance request saved successfully.");
        navigate("/myrequests");
      })
      .catch((error) => {
        const errorMessage = error.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join("; ")
          : error.message;
        toast.error(`Failed to save salary advance request: ${errorMessage}`);
      });
  };

  // Handle submit form
  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isFormValid) {
      toast.error("Please fill in all required fields.");
      return;
    }
    if (!requestTypeId) {
      toast.error("Request Type ID is missing.");
      return;
    }
    if (!userId) {
      toast.error("User ID is missing. Please log in again.");
      return;
    }

    const requestData = {
      amount: parseFloat(inputs.amount),
      currency: inputs.currency,
      purpose: inputs.purpose,
      isOnSaved: false,
      requestType: parseInt(requestTypeId),
      userId: parseInt(userId),
    };

    dispatch(addSalaryAdvanceRequest(requestData))
      .then(() => {
        setInputs({ amount: "", currency: "", purpose: "" });
        toast.success("Salary advance request submitted successfully.");
        navigate("/myrequests");
      })
      .catch((error) => {
        const errorMessage = error.response?.data?.errors
          ? Object.values(error.response.data.errors).flat().join("; ")
          : error.message;
        toast.error(`Failed to submit salary advance request: ${errorMessage}`);
      });
  };

  return (
    <div className={styles["component-wrapper"]}>
      <div className={styles["component-title"]}>Salary Advance</div>
      <div className={styles["generic-activity-component-description"]}>
        Create a new salary advance request by filling out the form below and
        submitting when you are done. Ensure all fields have accurate
        information.
      </div>
      <div className={styles["generic_request_content_component_wrapper"]}>
        <form
          className={styles["generic-activity-component-form"]}
          onSubmit={handleSubmit}
        >
          <label className={styles["form-label"]} htmlFor="addrequestamount">
            <MonetizationOnRounded /> Amount
          </label>
          <section className={styles["form-section"]}>
            <input
              type="number"
              name="amount"
              placeholder="Enter amount for request"
              id="addrequestamount"
              required
              value={inputs.amount || ""}
              onChange={handleChange}
              className={styles["form-input"]}
              aria-label="Amount"
            />
          </section>

          <label className={styles["form-label"]}>
            <PaymentsRounded /> Currency
          </label>
          <section className={styles["form-section"]}>
            <select
              name="currency"
              required
              value={inputs.currency || ""}
              onChange={handleChange}
              className={styles["form-select"]}
              aria-label="Currency"
            >
              <option value="">Select a currency for the amount</option>
              {currencies.map((currency) => (
                <option key={currency.id} value={currency.name}>
                  {currency.name}
                </option>
              ))}
            </select>
          </section>

          <label className={styles["form-label"]} htmlFor="addrequestpurpose">
            <LiveHelpRounded /> Purpose
          </label>
          <section className={styles["form-section"]}>
            <textarea
              name="purpose"
              placeholder="Enter purpose for request"
              id="addrequestpurpose"
              required
              value={inputs.purpose || ""}
              onChange={handleChange}
              className={styles["form-textarea"]}
              aria-label="Purpose"
            />
          </section>

          <div className={styles["button-row"]}>
            <button
              type="button"
              className={styles["generic-save-btn"]}
              onClick={handleSave}
              disabled={!isFormValid}
              aria-label="Save salary advance request"
            >
              Save
            </button>
            <button
              type="button"
              className={styles["generic-submit-btn"]}
              onClick={handleSubmit}
              disabled={!isFormValid}
              aria-label="Submit salary advance request"
            >
              Submit
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SalaryAdvance;
